"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  LayoutDashboard,
  Users,
  Wallet,
  Shield,
  Coins,
  Megaphone,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  Scale,
  BookOpen,
  Vault,
  Building2,
  GitCompareArrows,
  CalendarCheck,
  ClipboardList,
  Banknote,
  ClipboardCheck,
  ListChecks,
  ShieldCheck,
  GitBranch,
  Landmark,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth-store";
import { useState, useEffect } from "react";
import type { UserRole } from "@/types";

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  roles: UserRole[];
  badge?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: "개요",
    items: [
      {
        href: "/",
        label: "대시보드",
        icon: <LayoutDashboard className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
    ],
  },
  {
    title: "토큰증권",
    items: [
      {
        href: "/security-tokens",
        label: "토큰 목록",
        icon: <Shield className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
      // '토큰 발행' 메뉴는 계좌관리기관 모듈로 일원화 — 중복 제거.
      {
        href: "/public-offerings",
        label: "공모 관리",
        icon: <Megaphone className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER"],
      },
    ],
  },
  {
    title: "투자자",
    items: [
      {
        href: "/users",
        label: "사용자 관리",
        icon: <Users className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/wallets",
        label: "지갑 관리",
        icon: <Wallet className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
    ],
  },
  {
    title: "계좌관리기관",
    items: [
      {
        href: "/account-manager",
        label: "운영 현황",
        icon: <Vault className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/issue-token",
        label: "토큰 발행",
        icon: <Coins className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER"],
      },
      {
        href: "/account-manager/subscriptions",
        label: "청약 관리",
        icon: <ClipboardCheck className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER"],
      },
      {
        href: "/account-manager/customer-accounts",
        label: "고객계좌부",
        icon: <Users className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/deposits",
        label: "예치금 관리",
        icon: <Landmark className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/issuer-accounts",
        label: "발행인관리계좌부",
        icon: <Building2 className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/own-accounts",
        label: "자기계좌부",
        icon: <Banknote className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/rights",
        label: "권리행사 일정",
        icon: <CalendarCheck className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/beneficiary-register",
        label: "권리자명부",
        icon: <ListChecks className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/reconciliation",
        label: "KSD 대조",
        icon: <GitCompareArrows className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/compliance",
        label: "운영 컴플라이언스",
        icon: <ShieldCheck className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
      {
        href: "/account-manager/reports",
        label: "감독기관 보고",
        icon: <ClipboardList className="h-[18px] w-[18px]" />,
        roles: ["ADMIN"],
      },
    ],
  },
  {
    title: "동향",
    items: [
      {
        href: "/sto-news",
        label: "STO 동향",
        icon: <Newspaper className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
      {
        href: "/regulatory-tracker",
        label: "시행령 추적",
        icon: <GitBranch className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER"],
      },
      {
        href: "/regulations",
        label: "관련 법령",
        icon: <Scale className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
      {
        href: "/resources",
        label: "교육 자료",
        icon: <BookOpen className="h-[18px] w-[18px]" />,
        roles: ["ADMIN", "ISSUER", "INVESTOR"],
      },
    ],
  },
];

function NetworkStatus() {
  const [status, setStatus] = useState<"online" | "offline" | "checking">("checking");

  useEffect(() => {
    const check = async () => {
      try {
        // chain-first health check — issuer-accounts API (bridge → /getTokenList)
        const res = await fetch(`/api/account-manager/issuer-accounts?_t=${Date.now()}`, {
          method: "GET",
          cache: "no-store",
          signal: AbortSignal.timeout(5000),
        });
        setStatus(res.ok ? "online" : "offline");
      } catch {
        setStatus("offline");
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  const colors = {
    online: "bg-emerald-500",
    offline: "bg-red-500",
    checking: "bg-yellow-500 animate-pulse",
  };
  const labels = {
    online: "네트워크 정상",
    offline: "네트워크 오프라인",
    checking: "확인 중...",
  };

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
      <span className={cn("h-2 w-2 rounded-full", colors[status])} />
      <span>{labels[status]}</span>
    </div>
  );
}

export function Sidebar() {
  const pathname = usePathname();
  const { role } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        "flex h-screen flex-col border-r border-gray-200/80 bg-white transition-all duration-300 ease-in-out dark:border-gray-800 dark:bg-gray-950",
        collapsed ? "w-[72px]" : "w-[260px]"
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-gray-200/80 px-4 dark:border-gray-800">
        <Link
          href="/"
          className={cn(
            "flex items-center gap-2.5 transition-all",
            collapsed && "justify-center"
          )}
        >
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 shadow-sm">
            <Shield className="h-4 w-4 text-white" />
          </div>
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-[15px] font-bold tracking-tight text-gray-900 dark:text-gray-100">
                WonLET
              </span>
              <span className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                Admin Console
              </span>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {navGroups.map((group) => {
          const filteredItems = group.items.filter((item) =>
            item.roles.includes(role)
          );
          if (filteredItems.length === 0) return null;
          return (
            <div key={group.title} className="mb-5">
              {!collapsed && (
                <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {filteredItems.map((item) => {
                  const isActive =
                    pathname === item.href ||
                    (item.href !== "/" &&
                      pathname.startsWith(item.href) &&
                      !filteredItems.some(
                        (other) =>
                          other.href !== item.href &&
                          other.href.startsWith(item.href) &&
                          pathname.startsWith(other.href)
                      ));
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150",
                        isActive
                          ? "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300"
                          : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800/50 dark:hover:text-gray-200",
                        collapsed && "justify-center px-0"
                      )}
                      {...(collapsed ? { "data-tooltip": item.label } : {})}
                    >
                      {/* Active indicator bar */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-blue-600 dark:bg-blue-400" />
                      )}
                      <span
                        className={cn(
                          "flex-shrink-0 transition-colors",
                          isActive
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300"
                        )}
                      >
                        {item.icon}
                      </span>
                      {!collapsed && <span>{item.label}</span>}
                      {!collapsed && item.badge && (
                        <span className="ml-auto rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-200/80 px-3 py-3 dark:border-gray-800">
        {!collapsed && <NetworkStatus />}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className={cn(
            "mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-gray-400 transition-colors hover:bg-gray-50 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300",
            collapsed && "justify-center"
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <>
              <ChevronLeft className="h-4 w-4" />
              <span>사이드바 접기</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}
